<script lang="ts">
  import { afterUpdate, beforeUpdate, onDestroy } from 'svelte';
  import {
    analysisSeriesForSource,
    correlationBetween,
    differenceFromBaselineModel,
    normalizedComparisonModel,
    rawComparisonModel,
    rollingCorrelationModel,
    summarizeSeries,
    type AnalysisSeries,
    type AnalysisSummary,
    type CorrelationResult
  } from '../lib/analysis';
  import {
    waveformChoiceLabel,
    waveformChoicesForMode
  } from '../lib/event-waveform-series';
  import type {
    EventDetailsJob,
    EventDetailsJobResult,
    EventModelJob,
    EventModelJobResult
  } from '../lib/event-analysis-computation';
  import { EVENT_ONLY_ANALYSIS_MODES, isEventOnlyAnalysisMode } from '../lib/event-analysis-modes';
  import { eventAnalysisJobCacheKey, runEventAnalysisJob } from '../lib/event-analysis-runner';
  import { capturePanelResize, restoreViewportAfterPanelResize, type PanelResizeSnapshot } from '../lib/panel-transition';
  import { parameterById, plotByKind } from '../lib/registry';
  import type { AnalysisDataMode, AnalysisMode, AnalysisState, ChartModel, PlotAnalysisSource, PlotSpec } from '../lib/types';
  import AnalysisChart from './AnalysisChart.svelte';
  import AnalysisControls from './AnalysisControls.svelte';
  import AnalysisHeader from './AnalysisHeader.svelte';
  import AnalysisResults from './AnalysisResults.svelte';
  import type { AnalysisRow } from './analysis-panel-types';

  export let plots: PlotSpec[];
  export let sources: Record<string, PlotAnalysisSource>;
  export let waveformSources: Record<string, PlotAnalysisSource> = {};
  export let state: AnalysisState;
  export let onStateChange: (state: AnalysisState) => void;
  export let cursorPreview = 0;
  export let onCursorPreview: (cursor: number) => void = () => undefined;

  const MAX_SELECTED_PLOTS = 4;
  const MAX_LOCAL_CACHE_ENTRIES = 32;
  const extractedSeriesCache = new WeakMap<PlotAnalysisSource, Map<string, AnalysisSeries[]>>();
  const summaryCache = new WeakMap<AnalysisSeries, AnalysisSummary>();
  const correlationCache = new WeakMap<AnalysisSeries, WeakMap<AnalysisSeries, CorrelationResult>>();
  const seriesTokens = new WeakMap<AnalysisSeries, number>();
  const modelCache = new Map<string, ChartModel>();
  let nextSeriesToken = 1;
  let analysisDetailsOpen = false;
  let eventModelResult: EventModelJobResult | null = null;
  let eventModelResultKey = '';
  let scheduledEventModelKey = '';
  let eventModelGeneration = 0;
  let eventModelError = '';
  let eventDetailsResult: EventDetailsJobResult | null = null;
  let eventDetailsResultKey = '';
  let scheduledEventDetailsKey = '';
  let eventDetailsGeneration = 0;
  let eventDetailsError = '';
  let analysisPanelElement: HTMLElement;
  let resizeSnapshot: PanelResizeSnapshot | null = null;
  let resizeRestoreFrame: number | null = null;

  beforeUpdate(() => {
    if (!analysisPanelElement) return;
    if (resizeRestoreFrame != null) {
      window.cancelAnimationFrame(resizeRestoreFrame);
      resizeRestoreFrame = null;
    }
    if (resizeSnapshot) restoreViewportAfterPanelResize(analysisPanelElement, resizeSnapshot);
    resizeSnapshot = capturePanelResize(analysisPanelElement);
  });

  afterUpdate(() => {
    if (!analysisPanelElement || !resizeSnapshot) return;
    const snapshot = resizeSnapshot;
    if (!restoreViewportAfterPanelResize(analysisPanelElement, snapshot)) {
      resizeSnapshot = null;
      return;
    }
    resizeRestoreFrame = window.requestAnimationFrame(() => {
      resizeRestoreFrame = null;
      restoreViewportAfterPanelResize(analysisPanelElement, snapshot);
      if (resizeSnapshot === snapshot) resizeSnapshot = null;
    });
  });

  onDestroy(() => {
    if (resizeRestoreFrame != null) window.cancelAnimationFrame(resizeRestoreFrame);
  });

  function extractedSeries(source: PlotAnalysisSource, panelLabel: string): AnalysisSeries[] {
    let byLabel = extractedSeriesCache.get(source);
    if (!byLabel) {
      byLabel = new Map();
      extractedSeriesCache.set(source, byLabel);
    }
    const cached = byLabel.get(panelLabel);
    if (cached) return cached;
    const result = analysisSeriesForSource(source, panelLabel);
    byLabel.set(panelLabel, result);
    return result;
  }

  function seriesToken(series: AnalysisSeries): number {
    const existing = seriesTokens.get(series);
    if (existing != null) return existing;
    const token = nextSeriesToken++;
    seriesTokens.set(series, token);
    return token;
  }

  function retainModel(key: string, model: ChartModel): ChartModel {
    modelCache.delete(key);
    modelCache.set(key, model);
    while (modelCache.size > MAX_LOCAL_CACHE_ENTRIES) {
      const oldest = modelCache.keys().next().value as string | undefined;
      if (oldest == null) break;
      modelCache.delete(oldest);
    }
    return model;
  }

  function cachedComparisonModel(mode: AnalysisMode, series: AnalysisSeries[], windowSize: number): ChartModel {
    const key = `${mode}|${windowSize}|${series.map(seriesToken).join(',')}`;
    const cached = modelCache.get(key);
    if (cached) return retainModel(key, cached);
    let model: ChartModel;
    if (mode === 'normalized-comparison') model = normalizedComparisonModel(series);
    else if (mode === 'raw-comparison') model = rawComparisonModel(series);
    else if (mode === 'difference-from-baseline') model = differenceFromBaselineModel(series);
    else model = rollingCorrelationModel(series, windowSize);
    return retainModel(key, model);
  }

  function cachedSummary(series: AnalysisSeries): AnalysisSummary {
    const cached = summaryCache.get(series);
    if (cached) return cached;
    const summary = summarizeSeries(series);
    summaryCache.set(series, summary);
    return summary;
  }

  $: selectedSet = new Set(state.selectedPlotIds);
  $: rows = plots.map((plot, index): AnalysisRow => {
    const source = (state.dataMode === 'event-waveforms' ? waveformSources : sources)[plot.id];
    const panelLabel = state.dataMode === 'event-waveforms'
      ? `Plot ${index + 1}`
      : `Plot ${index + 1}: ${parameterById(plot.parameterId).name}`;
    const series = source ? extractedSeries(source, panelLabel) : [];
    const choices = state.dataMode === 'event-waveforms' ? waveformChoicesForMode(state.mode, series) : series;
    const selectedSeriesId = (state.dataMode === 'event-waveforms' ? state.waveformSeriesByPlot : state.seriesByPlot)[plot.id];
    return {
      plot,
      index,
      title: state.dataMode === 'event-waveforms'
        ? `${panelLabel} - Captured event waveform`
        : `${panelLabel} - ${plotByKind(plot.kind).shortName}`,
      selected: selectedSet.has(plot.id),
      source,
      series,
      choices,
      selectedSeries: choices.find((item) => item.id === selectedSeriesId) ?? choices[0]
    };
  });
  $: selectedSeries = rows.flatMap((row) => row.selected && row.selectedSeries ? [row.selectedSeries] : []);
  $: eventStatisticSeries = state.dataMode === 'event-waveforms' && analysisDetailsOpen
    ? rows.flatMap((row): AnalysisSeries[] => {
        if (!row.selected || !row.selectedSeries) return [];
        if (state.mode === 'calculated-power') {
          const phase = waveformChoiceLabel(state.mode, row.selectedSeries);
          return row.series.filter((series) => series.name === `Voltage: ${phase}` || series.name === `Current: ${phase}`);
        }
        if (state.mode === 'three-phase-balance') {
          const quantity = waveformChoiceLabel(state.mode, row.selectedSeries);
          return row.series.filter((series) => new RegExp(`^${quantity}: L[123]$`).test(series.name));
        }
        return [row.selectedSeries];
      })
    : [];
  $: selectedMissingEvent = state.dataMode === 'event-waveforms'
    && rows.some((row) => row.selected && !row.plot.selectedEvent);
  $: selectedPending = rows.some((row) => row.selected && !row.source
    && (state.dataMode === 'plot-data' || Boolean(row.plot.selectedEvent)));
  $: selectedUnavailable = rows.some((row) => row.selected && row.source && row.choices.length === 0);
  $: domainsCompatible = new Set(selectedSeries.map((series) => series.domainKey)).size <= 1;
  $: unitsCompatible = new Set(selectedSeries.map((series) => series.unit)).size <= 1;
  $: waveformQuantitiesCompatible = new Set(selectedSeries.map((series) => series.name)).size <= 1;
  $: unitCount = new Set(selectedSeries.map((series) => series.unit)).size;
  $: minimumSelectedSeries = state.mode === 'rolling-correlation'
    || state.mode === 'difference-from-baseline'
    || state.mode === 'event-ensemble' ? 2 : 1;
  $: requiresMatchingUnits = state.mode === 'difference-from-baseline' || state.mode === 'event-ensemble';
  $: maximumTwoUnits = state.mode === 'raw-comparison'
    || state.mode === 'rms-peak-envelope'
    || state.mode === 'frequency-spectrum';
  $: requiresOneSeries = state.mode === 'time-frequency';
  $: analysisSelectionReady = state.selectedPlotIds.length > 0
    && !selectedMissingEvent
    && !selectedPending
    && !selectedUnavailable
    && selectedSeries.length >= minimumSelectedSeries
    && (!requiresOneSeries || selectedSeries.length === 1)
    && domainsCompatible
    && (!requiresMatchingUnits || unitsCompatible)
    && (state.mode !== 'event-ensemble' || waveformQuantitiesCompatible)
    && (!maximumTwoUnits || unitCount <= 2);
  $: modeDescription = descriptionForMode(state.mode);
  $: eventModelJob = state.dataMode === 'event-waveforms'
    && isEventOnlyAnalysisMode(state.mode)
    && analysisSelectionReady
    ? {
        kind: 'model',
        mode: state.mode,
        selectedSeries,
        groups: rows.flatMap((row) => row.selected && row.selectedSeries
          ? [{ allSeries: row.series, representative: row.selectedSeries }]
          : [])
      } satisfies EventModelJob
    : null;
  $: currentEventModelKey = eventModelJob ? eventAnalysisJobCacheKey(eventModelJob) : '';
  $: if (!eventModelJob && scheduledEventModelKey) {
    eventModelGeneration += 1;
    scheduledEventModelKey = '';
    eventModelError = '';
  }
  $: if (eventModelJob && currentEventModelKey !== scheduledEventModelKey) {
    void calculateEventModel(eventModelJob, currentEventModelKey);
  }
  $: eventModelCurrent = eventModelResultKey === currentEventModelKey ? eventModelResult : null;
  $: eventModelPending = Boolean(eventModelJob && !eventModelCurrent && !eventModelError);
  $: powerResults = eventModelCurrent?.powerResults ?? [];
  $: balanceResults = eventModelCurrent?.balanceResults ?? [];
  $: analysisModel = isEventOnlyAnalysisMode(state.mode)
    ? eventModelCurrent?.model ?? { title: 'Event analysis', series: [] }
    : cachedComparisonModel(state.mode, selectedSeries, state.windowSize);
  $: eventDetailsJob = state.dataMode === 'event-waveforms' && analysisDetailsOpen && analysisSelectionReady
    ? { kind: 'details', selectedSeries, statisticSeries: eventStatisticSeries } satisfies EventDetailsJob
    : null;
  $: currentEventDetailsKey = eventDetailsJob ? eventAnalysisJobCacheKey(eventDetailsJob) : '';
  $: if (!eventDetailsJob && scheduledEventDetailsKey) {
    eventDetailsGeneration += 1;
    scheduledEventDetailsKey = '';
    eventDetailsError = '';
  }
  $: if (eventDetailsJob && currentEventDetailsKey !== scheduledEventDetailsKey) {
    void calculateEventDetails(eventDetailsJob, currentEventDetailsKey);
  }
  $: eventDetailsCurrent = eventDetailsResultKey === currentEventDetailsKey ? eventDetailsResult : null;
  $: eventDetailsPending = Boolean(eventDetailsJob && !eventDetailsCurrent && !eventDetailsError);
  $: eventStatistics = eventDetailsCurrent?.eventStatistics ?? [];
  $: alignmentStatistics = eventDetailsCurrent?.alignmentStatistics ?? [];
  $: summaries = analysisDetailsOpen
    ? selectedSeries.map((series) => ({ series, summary: cachedSummary(series) }))
    : [];

  async function calculateEventModel(job: EventModelJob, key: string): Promise<void> {
    scheduledEventModelKey = key;
    eventModelError = '';
    const generation = ++eventModelGeneration;
    try {
      const result = await runEventAnalysisJob(job);
      if (generation !== eventModelGeneration || key !== currentEventModelKey) return;
      eventModelResult = result;
      eventModelResultKey = key;
    } catch (reason) {
      if (generation !== eventModelGeneration || key !== currentEventModelKey) return;
      eventModelError = reason instanceof Error ? reason.message : 'Event analysis failed.';
    }
  }

  async function calculateEventDetails(job: EventDetailsJob, key: string): Promise<void> {
    scheduledEventDetailsKey = key;
    eventDetailsError = '';
    const generation = ++eventDetailsGeneration;
    try {
      const result = await runEventAnalysisJob(job);
      if (generation !== eventDetailsGeneration || key !== currentEventDetailsKey) return;
      eventDetailsResult = result;
      eventDetailsResultKey = key;
    } catch (reason) {
      if (generation !== eventDetailsGeneration || key !== currentEventDetailsKey) return;
      eventDetailsError = reason instanceof Error ? reason.message : 'Waveform statistics failed.';
    }
  }

  function descriptionForMode(selectedMode: AnalysisMode): string {
    if (selectedMode === 'rolling-correlation') return `Pearson r for every selected pair, calculated over ${state.windowSize} matched samples.`;
    if (selectedMode === 'normalized-comparison') return 'Centres each series on its mean and scales it by its standard deviation.';
    if (selectedMode === 'raw-comparison') return 'Overlays original values using up to two measurement units and y-axes.';
    if (selectedMode === 'difference-from-baseline') return 'Subtracts the first selected series from each remaining series. Axes and units must match.';
    if (selectedMode === 'rms-peak-envelope') return 'Plots one-cycle RMS and absolute peak envelopes without bridging recording gaps.';
    if (selectedMode === 'frequency-spectrum') return 'Shows a Hann-windowed amplitude spectrum from the longest continuous recording segment.';
    if (selectedMode === 'calculated-power') return 'Calculates instantaneous power from matching voltage and current samples for the selected phase.';
    if (selectedMode === 'three-phase-balance') return 'Estimates one-cycle negative- and zero-sequence components as a percentage of positive sequence.';
    if (selectedMode === 'time-frequency') return 'Shows a one-cycle, 50% overlapped spectrogram for one waveform, with magnitude in dB relative to one measurement unit.';
    return 'Shows the median and P05-P95 envelope across matching event waveforms.';
  }

  function togglePlot(plotId: string, checked: boolean) {
    const next = new Set(state.selectedPlotIds);
    if (checked) {
      if (next.size >= MAX_SELECTED_PLOTS) return;
      next.add(plotId);
    } else next.delete(plotId);
    onStateChange({ ...state, selectedPlotIds: plots.filter((plot) => next.has(plot.id)).map((plot) => plot.id) });
  }

  function chooseSeries(plotId: string, seriesId: string) {
    if (state.dataMode === 'event-waveforms') {
      onStateChange({ ...state, waveformSeriesByPlot: { ...state.waveformSeriesByPlot, [plotId]: seriesId } });
    } else {
      onStateChange({ ...state, seriesByPlot: { ...state.seriesByPlot, [plotId]: seriesId } });
    }
  }

  function chooseDataMode(dataMode: AnalysisDataMode) {
    if (dataMode !== state.dataMode) onStateChange({
      ...state,
      dataMode,
      mode: dataMode === 'plot-data' && EVENT_ONLY_ANALYSIS_MODES.has(state.mode) ? 'raw-comparison' : state.mode
    });
  }

  function chooseMode(value: string) {
    if (
      value === 'rolling-correlation'
      || value === 'normalized-comparison'
      || value === 'raw-comparison'
      || value === 'difference-from-baseline'
      || value === 'rms-peak-envelope'
      || value === 'frequency-spectrum'
      || value === 'calculated-power'
      || value === 'three-phase-balance'
      || value === 'time-frequency'
      || value === 'event-ensemble'
    ) onStateChange({ ...state, mode: value });
  }

  function selectionLabel(): string {
    if (state.mode === 'calculated-power') return 'Phase';
    if (state.mode === 'three-phase-balance') return 'Quantity';
    return 'Data series';
  }

  function setWindow(value: string) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) onStateChange({ ...state, windowSize: Math.max(3, Math.min(500, Math.round(parsed))) });
  }

  function correlationCell(left: AnalysisSeries, right: AnalysisSeries): CorrelationResult {
    if (left.id === right.id) return { value: 1, matchedSamples: left.data.length };
    const cached = correlationCache.get(left)?.get(right);
    if (cached) return cached;
    const result = correlationBetween(left, right);
    let leftCache = correlationCache.get(left);
    if (!leftCache) {
      leftCache = new WeakMap();
      correlationCache.set(left, leftCache);
    }
    leftCache.set(right, result);
    let rightCache = correlationCache.get(right);
    if (!rightCache) {
      rightCache = new WeakMap();
      correlationCache.set(right, rightCache);
    }
    rightCache.set(left, result);
    return result;
  }

</script>

<section bind:this={analysisPanelElement} id="workspace-analysis" class="analysis-panel" aria-labelledby="analysis-heading">
  <AnalysisHeader
    dataMode={state.dataMode}
    selectedPlotCount={state.selectedPlotIds.length}
    maximumSelectedPlots={MAX_SELECTED_PLOTS}
    onChooseDataMode={chooseDataMode}
  />

  <div class="analysis-panel__body">
    <AnalysisControls
      {rows}
      {state}
      maximumSelectedPlots={MAX_SELECTED_PLOTS}
      {modeDescription}
      selectionLabel={selectionLabel()}
      onTogglePlot={togglePlot}
      onChooseSeries={chooseSeries}
      onChooseMode={chooseMode}
      onSetWindow={setWindow}
    />

    <AnalysisChart
      {state}
      selectedSeriesCount={selectedSeries.length}
      {selectedMissingEvent}
      {selectedPending}
      {selectedUnavailable}
      {requiresOneSeries}
      {minimumSelectedSeries}
      {domainsCompatible}
      {requiresMatchingUnits}
      {unitsCompatible}
      {waveformQuantitiesCompatible}
      {maximumTwoUnits}
      {unitCount}
      {eventModelPending}
      eventModelAttempted={Boolean(eventModelJob)}
      {eventModelError}
      model={analysisModel}
      {cursorPreview}
      {onCursorPreview}
    />

    {#if selectedSeries.length}
      <AnalysisResults
        open={analysisDetailsOpen}
        dataMode={state.dataMode}
        {selectedSeries}
        {domainsCompatible}
        {eventDetailsPending}
        {eventDetailsError}
        {eventStatistics}
        {alignmentStatistics}
        {powerResults}
        {balanceResults}
        {summaries}
        {correlationCell}
        onOpenChange={(open) => analysisDetailsOpen = open}
      />
    {/if}

  </div>
</section>
